import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { API } from "../constants/api";
import useAuthAxios from "../utils/baseAxios";
import { isAssetOfferedByApi, resolveApiAssetCode } from "../utils/asset-config";

/**
 * The asset tickers this environment's backend actually trades, straight from
 * GET /api/rates.
 *
 * Our local `coins` list is the source of truth for names and logos, but not
 * for what to put on the wire: staging and production disagree on Polygon
 * (MATIC vs POL), so anything sent to the API should go through `toApiAsset`.
 *
 * Shares the "get-rates" query key with the other rate hooks, so this costs no
 * extra request.
 */
export const useApiAssets = () => {
  const axiosInstance = useAuthAxios();

  const { data: rates } = useQuery({
    queryKey: ["get-rates"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getRates);
      return response.data.data.rates;
    },
    retry: 1,
  });

  const apiCodes: string[] = useMemo(() => {
    if (!Array.isArray(rates)) return [];
    return rates
      .map((rate: { abbr?: string }) => rate?.abbr?.trim().toUpperCase())
      .filter((abbr: string | undefined): abbr is string => !!abbr);
  }, [rates]);

  const toApiAsset = useCallback(
    (currency?: string | null) => resolveApiAssetCode(currency, apiCodes),
    [apiCodes]
  );

  const isOffered = useCallback(
    (currency?: string | null) => isAssetOfferedByApi(currency, apiCodes),
    [apiCodes]
  );

  return { apiCodes, toApiAsset, isOffered };
};

export default useApiAssets;
